import { ResolveFn } from '@angular/router';
import { inject } from '@angular/core';
import { map, of, switchMap } from 'rxjs';
import { Assessment, Languages, Norms, Package, Participant, Project, Report } from './models';
import { AssessmentDomainService } from './domain-services/assessment-domain.service';
import { ProjectDomainService } from './domain-services/project-domain.service';
import { ParticipantDomainService } from './domain-services/participant-domain.service';
import { ReportDomainService } from './domain-services/report-domain.service';
import { PackageDomainService } from './domain-services/package-domain.service';

export const assessments: ResolveFn<Assessment[]> = () =>
  inject(AssessmentDomainService).loadAssessments();

export const projects: ResolveFn<Project[]> = () => inject(ProjectDomainService).loadProjects();

export const project: ResolveFn<Project> = (route) => {
  const projectId = route.paramMap.get('projectId') ?? '';
  return inject(ProjectDomainService).loadProject(projectId);
};

export const projectParticipants: ResolveFn<Participant[]> = (route) => {
  const projectId = route.paramMap.get('projectId') ?? '';
  return inject(ParticipantDomainService).loadProjectParticipants(projectId);
};

export const projectReports: ResolveFn<Report[]> = (route) => {
  const projectId = route.paramMap.get('projectId') ?? '';
  return inject(ReportDomainService).loadProjectReports(projectId);
};

export const projectReport: ResolveFn<Report> = (route) => {
  const projectId = route.paramMap.get('projectId') ?? '';
  const reportId = route.paramMap.get('reportId') ?? '';
  return inject(ReportDomainService).loadProjectReport(projectId, reportId);
};

export const projectAssessments: ResolveFn<Assessment[]> = (route) => {
  const projectId = route.paramMap.get('projectId') ?? '';
  return inject(AssessmentDomainService).loadProjectAssessments(projectId);
};

export const reportLanguages: ResolveFn<Languages> = (route) => {
  const reportId = route.paramMap.get('reportId') ?? '';
  return inject(ReportDomainService).loadReportLanguages(reportId);
};

export const reportNorms: ResolveFn<Norms> = (route) => {
  const reportId = route.paramMap.get('reportId') ?? '';
  return inject(ReportDomainService).loadReportNorms(reportId);
};

export const projectPackages: ResolveFn<Package[]> = (route) => {
  const projectId = route.paramMap.get('projectId') ?? '';
  return inject(PackageDomainService).loadProjectPackages(projectId);
};

export const participantReports: ResolveFn<Report[]> = (route) => {
  const participantId = route.paramMap.get('participantId') ?? '';
  return inject(ReportDomainService).loadParticipantReports(participantId);
};

export const participantPackages: ResolveFn<Package[]> = (route) => {
  const participantId = route.paramMap.get('participantId') ?? '';
  return inject(PackageDomainService).loadParticipantPackages(participantId);
};

export const clientReports: ResolveFn<Report[]> = () =>
  inject(ReportDomainService).loadClientReports();

export const clientPackages: ResolveFn<Package[]> = () =>
  inject(PackageDomainService).loadClientPackages();

export const report: ResolveFn<Report> = (route) => {
  const reportId = route.paramMap.get('reportId') ?? '';
  const participantId =
    route.paramMap.get('participantId') ?? route.parent?.paramMap.get('participantId') ?? null;
  const projectDomain = inject(ProjectDomainService);
  const currentProject = projectDomain.currentProject();
  const projectId = currentProject?.id ?? route.paramMap.get('projectId') ?? '';
  const reportDomain = inject(ReportDomainService);
  const packageDomain = inject(PackageDomainService);

  if (participantId) {
    return packageDomain.loadParticipantPackages(participantId).pipe(
      switchMap((packages) => {
        const pkg = packages.find((p) => p.id === reportId);
        if (pkg) {
          return of(pkg as Report);
        }

        return reportDomain.loadParticipantReports(participantId).pipe(
          map((reports) => {
            const found = reports.find((r) => r.id === reportId);
            if (!found) {
              throw new Error('Report not found for participant');
            }
            return found;
          }),
        );
      }),
    );
  }

  return packageDomain.loadProjectPackages(projectId).pipe(
    switchMap((packages) => {
      const pkg = packages.find((p) => p.id === reportId);
      if (pkg) {
        return of(pkg as Report);
      }
      return reportDomain.loadProjectReport(projectId, reportId);
    }),
  );
};
